/**
 * Luxe · Clearance Drag/Snap Resolver (canonical mm, pure)
 *
 * Single geometry definition consumed by:
 *   • the visual 2D dashed envelope,
 *   • drag/move Blocking clamp,
 *   • fast-drag anti-tunnelling (swept AABB),
 *   • diagonal/corner deterministic resolution,
 *   • final-drop validation,
 *   • Advisory / Warn / Block conflict feedback.
 *
 * Coordinate system (matches SchematicBuilder / DesignScene):
 *   X = horizontal along wall (mm), 0 at left.
 *   Y = height above floor (mm), 0 at floor (FFL), Y grows UPWARD.
 *   Envelope grows outward from the item's canonical rectangle.
 *
 * This module NEVER mutates its inputs.
 */

/**
 * Canonical clearance envelope for a scene item.
 * Returns { left, right, bottom, top, enforcement } in canonical mm,
 * or null when clearance is disabled / all-zero / owner is missing
 * geometry. Envelope grows OUTWARD from the item's rectangle.
 */
export function getClearanceEnvelope(item) {
  if (!item) return null;
  const cc = item.clearance_config;
  if (!cc || !cc.enabled) return null;
  const t = Number(cc.top_mm) || 0;
  const b = Number(cc.bottom_mm) || 0;
  const l = Number(cc.left_mm) || 0;
  const r = Number(cc.right_mm) || 0;
  if (t + b + l + r === 0) return null;
  const x = Number(item.x) || 0;
  const y = Number(item.y) || 0;
  const w = Number(item.width) || 0;
  const h = Number(item.height) || 0;
  return {
    left:   x - l,
    right:  x + w + r,
    bottom: y - b,
    top:    y + h + t,
    enforcement: cc.enforcement || "warn",
  };
}

/**
 * Does the axis-aligned rectangle at (x, y, w, h) overlap the given
 * envelope? Edges touching (component's edge flush with envelope
 * boundary) are considered LEGAL (not overlapping).
 */
export function rectOverlapsEnvelope(x, y, w, h, env) {
  return (
    x < env.right &&
    x + w > env.left &&
    y < env.top &&
    y + h > env.bottom
  );
}

/**
 * Blocking clamp resolver — swept AABB against Minkowski-expanded
 * envelopes.
 *
 * Strategy:
 *   For every Blocking envelope, expand it by the dragged rectangle's
 *   extents (Minkowski sum) so the moving object collapses to a POINT
 *   at (orig.x, orig.y). We then perform a slab test against the
 *   swept SEGMENT from `orig` to `cand`. This detects both:
 *     • cand overlaps envelope (short drag into zone), and
 *     • segment passes ENTIRELY THROUGH envelope (fast drag), even
 *       when `cand` itself lies outside the envelope on the far side.
 *   On collision we clamp only the ENTERING AXIS (whichever slab was
 *   entered latest) so the component slides along the parallel axis —
 *   giving natural corner behaviour and matching the brief's "pointer
 *   may continue moving; component remains held at boundary" rule.
 *
 *   Legacy overlap: if `orig` was already inside the expanded blocker
 *   (loaded scene with historic overlap) the envelope is skipped so
 *   the user can still drag out of it.
 *
 *   Iterates up to 8 passes so multi-envelope corner resolutions
 *   converge deterministically.
 *
 * @param {{x:number,y:number}} orig — pre-drag legal top-left (mm)
 * @param {{x:number,y:number}} cand — snap-aligned candidate top-left
 * @param {number} wDrag — dragged item width (mm)
 * @param {number} hDrag — dragged item height (mm)
 * @param {Array<{left:number,right:number,bottom:number,top:number}>} blockers
 * @returns {{x:number,y:number,clampedAxes:{x:boolean,y:boolean}}}
 */
export function resolveBlockingClamp(orig, cand, wDrag, hDrag, blockers) {
  let rx = Number(cand.x) || 0;
  let ry = Number(cand.y) || 0;
  const oX = Number(orig.x) || 0;
  const oY = Number(orig.y) || 0;
  let clampedX = false;
  let clampedY = false;
  if (!blockers || blockers.length === 0) {
    return { x: rx, y: ry, clampedAxes: { x: false, y: false } };
  }
  const EPS = 1e-9;
  for (let iter = 0; iter < 8; iter++) {
    let changed = false;
    for (const env of blockers) {
      // Minkowski-expanded blocker for top-left-corner motion.
      const exLeft   = env.left   - wDrag;
      const exRight  = env.right;
      const exBottom = env.bottom - hDrag;
      const exTop    = env.top;
      // Legacy overlap — orig already inside expanded blocker. Preserve
      // historic geometry, do NOT clamp against this envelope.
      if (
        oX > exLeft + EPS && oX < exRight - EPS &&
        oY > exBottom + EPS && oY < exTop - EPS
      ) continue;
      // Swept slab test on segment (orig → resolved candidate).
      const dx = rx - oX;
      const dy = ry - oY;
      let txEnter, txExit;
      if (Math.abs(dx) < EPS) {
        // Motion parallel to Y — X-slab overlap must hold throughout.
        if (oX <= exLeft + EPS || oX >= exRight - EPS) continue;
        txEnter = -Infinity; txExit = Infinity;
      } else {
        const t1 = (exLeft  - oX) / dx;
        const t2 = (exRight - oX) / dx;
        txEnter = Math.min(t1, t2);
        txExit  = Math.max(t1, t2);
      }
      let tyEnter, tyExit;
      if (Math.abs(dy) < EPS) {
        if (oY <= exBottom + EPS || oY >= exTop - EPS) continue;
        tyEnter = -Infinity; tyExit = Infinity;
      } else {
        const t1 = (exBottom - oY) / dy;
        const t2 = (exTop    - oY) / dy;
        tyEnter = Math.min(t1, t2);
        tyExit  = Math.max(t1, t2);
      }
      const tEnter = Math.max(txEnter, tyEnter);
      const tExit  = Math.min(txExit, tyExit);
      if (tEnter > tExit) continue;             // no intersection
      if (tEnter >= 1 - EPS) continue;          // collision beyond candidate
      if (tEnter < -EPS) continue;              // orig already inside (should be caught above)
      // Determine ENTERING axis — whichever slab was entered latest.
      const enteringX = txEnter >= tyEnter;
      if (enteringX) {
        // Moving right (dx>0) → enters left edge of ex → clamp to exLeft.
        // Moving left  (dx<0) → enters right edge → clamp to exRight.
        rx = dx > 0 ? exLeft : exRight;
        clampedX = true;
      } else {
        ry = dy > 0 ? exBottom : exTop;
        clampedY = true;
      }
      changed = true;
    }
    if (!changed) break;
  }
  return { x: rx, y: ry, clampedAxes: { x: clampedX, y: clampedY } };
}

/**
 * Conflict scan — checks the dragged rectangle against every other
 * clearance-enabled item and returns the ids of conflicting owners
 * bucketed by enforcement level. Advisory / Warn conflicts do NOT
 * clamp position; they surface visual feedback only. Blocking
 * conflicts are additionally clamped by `resolveBlockingClamp`.
 */
export function computeConflicts(dragId, x, y, w, h, allItems) {
  const out = { blocking: [], warn: [], advisory: [] };
  if (!Array.isArray(allItems)) return out;
  for (const other of allItems) {
    if (!other || other.id === dragId) continue;
    const env = getClearanceEnvelope(other);
    if (!env) continue;
    if (!rectOverlapsEnvelope(x, y, w, h, env)) continue;
    const level = env.enforcement === "block"
      ? "blocking"
      : env.enforcement === "advisory"
        ? "advisory"
        : "warn";
    out[level].push(other.id);
  }
  return out;
}

/**
 * Blocking envelopes for every OTHER item — helper used by the drag
 * pipeline to feed `resolveBlockingClamp`.
 */
export function collectBlockingEnvelopes(dragId, allItems) {
  const out = [];
  if (!Array.isArray(allItems)) return out;
  for (const other of allItems) {
    if (!other || other.id === dragId) continue;
    const env = getClearanceEnvelope(other);
    if (!env) continue;
    if (env.enforcement !== "block") continue;
    out.push(env);
  }
  return out;
}

/**
 * ALL enabled Clearance envelopes (Advisory / Warn / Blocking) from
 * OTHER items — used by the drag pipeline for magnetic snap and by
 * the persistent overlay layer for always-visible dashed rendering.
 */
export function collectAllClearanceEnvelopes(dragId, allItems) {
  const out = [];
  if (!Array.isArray(allItems)) return out;
  for (const other of allItems) {
    if (!other || other.id === dragId) continue;
    const env = getClearanceEnvelope(other);
    if (!env) continue;
    out.push({ ...env, owner_id: other.id });
  }
  return out;
}

/**
 * Magnetic snap to the nearest Clearance boundary within `tolerance`
 * canonical mm.  Snaps physical edges (not centre) to exact canonical
 * boundary lines.  Returns `{x, y, snappedEdges: {left,right,top,bottom}}`
 * where each edge flag is true when that boundary of the dragged item
 * snapped to a matching Clearance boundary.
 *
 * Snap semantics:
 *   • dragged.LEFT   → env.right  (approaching from the right side)
 *   • dragged.RIGHT  → env.left   (approaching from the left side)
 *   • dragged.BOTTOM → env.top    (approaching from above)
 *   • dragged.TOP    → env.bottom (approaching from below)
 *
 * Multi-target: for each axis picks the nearest matching boundary
 * deterministically (smaller absolute delta wins; ties broken by
 * lower owner_id string).  Does not oscillate.
 *
 * Corners: both X and Y may snap simultaneously if both are within
 * tolerance.
 */
export function snapToClearanceBoundaries(x, y, w, h, envelopes, toleranceMm) {
  const tol = Math.max(0, Number(toleranceMm) || 0);
  if (!tol || !envelopes || envelopes.length === 0) {
    return { x, y, snappedEdges: {}, snappedTo: [] };
  }
  const dragged = { left: x, right: x + w, bottom: y, top: y + h };
  const candidates = { x: null, y: null };
  const snappedEdges = {};
  const snappedTo = [];
  for (const env of envelopes) {
    // X axis — dragged LEFT snaps to env.right
    const dxL = Math.abs(dragged.left - env.right);
    if (dxL <= tol) {
      const newX = env.right;
      const c = { newX, delta: dxL, side: "left", ownerId: env.owner_id };
      if (!candidates.x
          || c.delta < candidates.x.delta
          || (c.delta === candidates.x.delta && String(env.owner_id) < String(candidates.x.ownerId))) {
        candidates.x = c;
      }
    }
    // X axis — dragged RIGHT snaps to env.left
    const dxR = Math.abs(dragged.right - env.left);
    if (dxR <= tol) {
      const newX = env.left - w;
      const c = { newX, delta: dxR, side: "right", ownerId: env.owner_id };
      if (!candidates.x
          || c.delta < candidates.x.delta
          || (c.delta === candidates.x.delta && String(env.owner_id) < String(candidates.x.ownerId))) {
        candidates.x = c;
      }
    }
    // Y axis — dragged BOTTOM snaps to env.top
    const dyB = Math.abs(dragged.bottom - env.top);
    if (dyB <= tol) {
      const newY = env.top;
      const c = { newY, delta: dyB, side: "bottom", ownerId: env.owner_id };
      if (!candidates.y
          || c.delta < candidates.y.delta
          || (c.delta === candidates.y.delta && String(env.owner_id) < String(candidates.y.ownerId))) {
        candidates.y = c;
      }
    }
    // Y axis — dragged TOP snaps to env.bottom
    const dyT = Math.abs(dragged.top - env.bottom);
    if (dyT <= tol) {
      const newY = env.bottom - h;
      const c = { newY, delta: dyT, side: "top", ownerId: env.owner_id };
      if (!candidates.y
          || c.delta < candidates.y.delta
          || (c.delta === candidates.y.delta && String(env.owner_id) < String(candidates.y.ownerId))) {
        candidates.y = c;
      }
    }
  }
  let outX = x, outY = y;
  if (candidates.x) {
    outX = candidates.x.newX;
    snappedEdges[candidates.x.side] = true;
    snappedTo.push({ axis: "x", ...candidates.x });
  }
  if (candidates.y) {
    outY = candidates.y.newY;
    snappedEdges[candidates.y.side] = true;
    snappedTo.push({ axis: "y", ...candidates.y });
  }
  return { x: outX, y: outY, snappedEdges, snappedTo };
}
